import { ActionIcon, Box, Group, Image, Stack, Title } from "@mantine/core";
import { useHover } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconRobotFace, IconTrash, IconUser } from "@tabler/icons-react";
import type { JSX } from "react";
import { Fragment, useMemo } from "react";
import { useLocation, useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import Loading from "../Loading";
import classes from "../sharedItems/SharedItem.module.css";
import CopyButton from "./CopyButton";
import DownloadButton from "./DownloadButton";
import NewIterationForm from "./NewIterationForm";
import {
    type ImageThreadDeleteMutation,
    type ImageThreadDeleteMutationVariables,
    type ImageThreadQuery,
    type ImageThreadQueryVariables,
    imageThreadDeleteMutation,
    imageThreadQuery,
} from "./queries";

const ICON_SIZE = 32;

function TextBox({ text }: { text: string }): JSX.Element {
    return (
        <Box className={classes.container}>
            <Box>
                <IconUser size={ICON_SIZE} />
            </Box>
            <Box className={classes.userInput}>{text}</Box>
        </Box>
    );
}

function ImageBox({ file }: { file: { src: string; fileName: string } | null }): JSX.Element | null {
    const { hovered, ref } = useHover();
    if (!file?.src) {
        return null;
    }
    return (
        <Box className={classes.container} ref={ref}>
            {hovered && (
                <ActionIcon.Group className={classes.icons}>
                    <CopyButton image={{ file }} />
                    <DownloadButton image={{ file }} />
                </ActionIcon.Group>
            )}
            <Box>
                <IconRobotFace size={ICON_SIZE} />
            </Box>
            <Image src={file.src} radius="sm" fit="contain" />
        </Box>
    );
}

function ImageThreadView(): JSX.Element {
    const params = useParams();
    const navigate = useNavigate();
    const { search } = useLocation();
    const id = useMemo(() => Number(params.id), [params]);
    const [result, reexecute] = useQuery<ImageThreadQuery, ImageThreadQueryVariables>({
        query: imageThreadQuery,
        variables: { id },
    });
    const [, executeDelete] = useMutation<ImageThreadDeleteMutation, ImageThreadDeleteMutationVariables>(
        imageThreadDeleteMutation,
    );

    if (!Number.isFinite(id)) {
        navigate("/image-threads");
        return <Loading />;
    }

    const onDelete = async () => {
        const res = await executeDelete({ imageThreadId: id });
        if (res.error) {
            notifications.show({ title: "Failed to delete", message: res.error.message });
        } else {
            notifications.show({ title: "Thread deleted", message: `Thread #${id} deleted` });
            navigate({ pathname: "/image-threads", search });
        }
    };

    const confirmDelete = () =>
        modals.openConfirmModal({
            title: "Delete thread?",
            labels: { confirm: "Delete", cancel: "Cancel" },
            confirmProps: { color: "red" },
            onConfirm: onDelete,
        });

    if (result.fetching) {
        return <Loading />;
    }
    if (result.error) {
        return <Box>Error: {result.error.message}</Box>;
    }
    if (!result.data || !result.data.imageThread) {
        return <Box>Thread not found</Box>;
    }

    const edges = result.data.imageThread.iterations.edges;
    const iterations = edges.map((e) => e.node);
    const latest = iterations.length > 0 ? iterations[iterations.length - 1] : undefined;

    return (
        <Stack maw={900} mx="auto">
            <Group justify="space-between">
                <Title order={2}>Thread #{id}</Title>
                <ActionIcon color="red" variant="light" onClick={confirmDelete}>
                    <IconTrash size={16} />
                </ActionIcon>
            </Group>
            <Stack>
                {iterations.map((it) => (
                    <Fragment key={it.id}>
                        <TextBox text={it.userText} />
                        <ImageBox file={it.file} />
                    </Fragment>
                ))}
            </Stack>
            <NewIterationForm
                imageThreadId={id}
                defaults={{
                    size: latest?.size ?? undefined,
                    quality: latest?.quality ?? undefined,
                }}
                onCreated={() => reexecute({ requestPolicy: "network-only" })}
            />
            <Link to={{ pathname: "/image-threads", search }}>Back</Link>
        </Stack>
    );
}

export default ImageThreadView;
